
const VALID_EXTENSIONS = ['jpg', 'jpeg', 'png', 'heic'];
/* HEIC files often report an empty MIME type (especially on Windows),
   so the extension check above is the source of truth. */
const VALID_MIME_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', ''];
const REASONS_DEFAULT = '';

class ReturnRequestApp extends HTMLElement {
  connectedCallback() {
    this.maxFileMb = parseFloat(this.dataset.maxFileMb) || 5;
    this.uploadsInFlight = 0;
    this.uploadSeq = 0;
    this.matchedOrder = null;

    /* When the customer hits the browser Back button, the page may be restored
       from the bfcache with the success message + filled form still showing.
       Reload so they get a clean, empty form like at the beginning. */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) window.location.reload();
    });

    if (this.querySelector('[data-contact-posted]')) {
      this.showSuccess();
      return;
    }

    this.form = this.querySelector('[data-return-form]');
    this.rowTemplate = this.querySelector('[data-item-row]');
    if (!this.form || !this.rowTemplate) return;

    const parsed = this.buildOrders();
    this.orders = parsed.list;
    this.orderIndex = parsed.index;

    this.orderInput = this.form.querySelector('[data-order-input]');
    this.orderSuggestions = this.form.querySelector('[data-order-suggestions]');
    this.orderStatus = this.form.querySelector('[data-order-status]');
    this.itemsContainer = this.form.querySelector('[data-items]');

    this.orderInput.addEventListener('input', () => {
      this.renderOrderSuggestions();
      this.onOrderChange();
    });
    this.orderInput.addEventListener('focus', () => this.renderOrderSuggestions());

    this.form.querySelector('[data-add-item]').addEventListener('click', () => this.addItem());

    const submitButton = this.form.querySelector('[data-submit-button]');
    submitButton.addEventListener('click', (event) => {
      if (!this.prepareSubmission()) {
        event.preventDefault();
        return;
      }
      submitButton.classList.add('is-loading');
      submitButton.textContent = 'Sending…';
      setTimeout(() => {
        submitButton.classList.remove('is-loading');
        submitButton.textContent = 'Submit Return Request';
        this.showFormError(
          'Almost done — please click "Submit Return Request" once more to continue to the security check.'
        );
      }, 8000);
    });

    this.form.addEventListener('submit', (event) => event.preventDefault());

    /* Close any open suggestions when clicking elsewhere. */
    document.addEventListener('click', (event) => {
      this.querySelectorAll('[data-order-suggestions], [data-product-suggestions]').forEach((list) => {
        if (!list.closest('.return-request__combo').contains(event.target)) list.hidden = true;
      });
    });

    this.addItem();
  }

  /* ---- Orders data ---- */

  buildOrders() {
    const index = {};
    const list = [];
    const node = this.querySelector('[data-orders]');
    if (!node) return { list, index };
    let raw;
    try {
      raw = JSON.parse(node.textContent.replace(/,(\s*[}\]])/g, '$1'));
    } catch (error) {
      return { list, index };
    }
    raw.forEach((order) => {
      const items = (order.items || []).filter((it) => it.title || it.sku);
      const skus = {};
      items.forEach((it) => {
        if (it.sku) skus[it.sku] = (skus[it.sku] || 0) + Number(it.qty || 0);
      });
      const record = { name: order.name, number: order.number, date: order.date, items, skus };
      list.push(record);
      [order.name, order.number].forEach((key) => {
        const norm = this.normalizeOrderKey(key);
        if (norm) index[norm] = record;
      });
    });
    return { list, index };
  }

  normalizeOrderKey(value) {
    return String(value == null ? '' : value)
      .trim()
      .replace(/^#/, '')
      .toUpperCase();
  }

  /* ---- Order autosuggest ---- */

  renderOrderSuggestions() {
    const list = this.orderSuggestions;
    const query = this.normalizeOrderKey(this.orderInput.value);
    list.innerHTML = '';
    const matches = this.orders
      .filter((o) => {
        if (!query) return true;
        return (
          this.normalizeOrderKey(o.name).includes(query) ||
          this.normalizeOrderKey(o.number).includes(query)
        );
      })
      .slice(0, 8);
    if (matches.length === 0) {
      list.hidden = true;
      return;
    }
    matches.forEach((order) => {
      const li = document.createElement('li');
      li.className = 'return-request__suggestion';
      li.setAttribute('role', 'option');
      li.innerHTML = `<span>${this.escape(order.name)}</span><small>${this.escape(order.date)}</small>`;
      li.addEventListener('click', () => {
        this.orderInput.value = order.name;
        list.hidden = true;
        this.onOrderChange();
      });
      list.appendChild(li);
    });
    list.hidden = false;
  }

  onOrderChange() {
    this.showError(this.form, 'order', '');
    const value = this.orderInput.value.trim();
    this.matchedOrder = value ? this.orderIndex[this.normalizeOrderKey(value)] || null : null;
    if (!value) {
      this.setOrderStatus('', '');
    } else if (this.matchedOrder) {
      this.setOrderStatus(`Order ${this.matchedOrder.name} found — pick its products below or add your own.`, 'found');
    } else {
      this.setOrderStatus(
        "That's not an order number we have on your account — add the product details below and we'll review it.",
        'manual'
      );
    }
    /* Re-evaluate each row's "belongs to order" warning. */
    this.form.querySelectorAll('[data-return-item]').forEach((item) => this.updateWarning(item));
  }

  setOrderStatus(message, state) {
    if (!this.orderStatus) return;
    this.orderStatus.textContent = message || '';
    this.orderStatus.hidden = !message;
    this.orderStatus.dataset.state = state || '';
  }

  /* ---- Item rows ---- */

  addItem() {
    const item = this.rowTemplate.content.firstElementChild.cloneNode(true);
    this.itemsContainer.appendChild(item);
    this.bindItemListeners(item);
    this.refreshRemoveButtons();
  }

  removeItem(item) {
    item.remove();
    this.refreshRemoveButtons();
  }

  refreshRemoveButtons() {
    const items = this.form.querySelectorAll('[data-return-item]');
    items.forEach((item, i) => {
      const btn = item.querySelector('[data-remove-item]');
      if (btn) btn.hidden = items.length <= 1 || i === 0;
      const title = item.querySelector('[data-item-title]') || item.querySelector('.return-request__item-title');
      if (title) title.textContent = `Product ${i + 1}`;
    });
  }

  bindItemListeners(item) {
    const search = item.querySelector('[data-product-search]');
    const skuInput = item.querySelector('[data-sku-input]');
    const qtyInput = item.querySelector('[data-qty-input]');
    const reasonSelect = item.querySelector('[data-reason-select]');
    const photoInput = item.querySelector('[data-photo-input]');
    const photoLabel = item.querySelector('[data-photo-label]');
    const variantSelect = item.querySelector('[data-variant-select]');
    const removeBtn = item.querySelector('[data-remove-item]');

    let searchTimer = null;
    search.addEventListener('input', () => {
      item.querySelector('[data-product-value]').value = '';
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => this.renderProductSuggestions(item), 220);
    });
    search.addEventListener('focus', () => this.renderProductSuggestions(item));

    skuInput.addEventListener('input', () => this.updateWarning(item));
    qtyInput.addEventListener('input', () => this.validateQuantity(item));
    reasonSelect.addEventListener('change', () => this.validateReason(item));
    photoInput.addEventListener('change', () => this.handlePhotoChange(item));

    if (photoLabel) {
      photoLabel.addEventListener('click', () => photoInput.click());
      photoLabel.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          photoInput.click();
        }
      });
      photoLabel.setAttribute('role', 'button');
      photoLabel.setAttribute('tabindex', '0');
    }

    if (variantSelect) {
      variantSelect.addEventListener('change', () => {
        const opt = variantSelect.selectedOptions[0];
        if (!opt) return;
        skuInput.value = opt.dataset.sku || '';
        item.querySelector('[data-product-value]').value = opt.dataset.product || '';
        this.updateWarning(item);
      });
    }

    if (removeBtn) removeBtn.addEventListener('click', () => this.removeItem(item));
  }

  /* ---- Product autocomplete ---- */

  async renderProductSuggestions(item) {
    const search = item.querySelector('[data-product-search]');
    const list = item.querySelector('[data-product-suggestions]');
    const query = search.value.trim();
    const q = query.toLowerCase();
    list.innerHTML = '';

    const entries = [];

    /* Order's own items first (when an order is matched). */
    if (this.matchedOrder) {
      this.matchedOrder.items
        .filter((it) => !q || (it.title || '').toLowerCase().includes(q) || (it.sku || '').toLowerCase().includes(q))
        .forEach((it) =>
          entries.push({
            label: it.title || it.sku,
            sub: `From this order • SKU ${it.sku || '—'} • Qty ${it.qty}`,
            product: it.title || '',
            sku: it.sku || '',
            qty: it.qty,
            fromOrder: true,
          })
        );
    }

    /* Catalog search (whole store), filtered client-side from /products.json
       so it works even when Shopify's predictive search isn't enabled. */
    if (q.length >= 1) {
      const catalog = await this.ensureCatalog();
      const seen = new Set(entries.map((e) => (e.sku || e.product || '').toLowerCase()));
      catalog.forEach((product) => {
        const titleMatch = product.title.toLowerCase().includes(q);
        const variantMatch = product.variants.some(
          (v) => (v.sku || '').toLowerCase().includes(q) || (v.title || '').toLowerCase().includes(q)
        );
        if (!titleMatch && !variantMatch) return;
        if (seen.has(product.title.toLowerCase())) return;
        entries.push({
          label: product.title,
          sub: product.variants.length > 1 ? `From catalog • ${product.variants.length} options` : 'From catalog',
          product,
          fromOrder: false,
        });
      });
    }

    if (entries.length === 0) {
      list.hidden = true;
      return;
    }

    entries.slice(0, 12).forEach((entry) => {
      const li = document.createElement('li');
      li.className = 'return-request__suggestion';
      li.setAttribute('role', 'option');
      li.innerHTML = `<span>${this.escape(entry.label)}</span><small>${this.escape(entry.sub)}</small>`;
      li.addEventListener('click', () => {
        list.hidden = true;
        if (entry.fromOrder) {
          this.applyOrderItem(item, entry);
        } else {
          this.applyCatalogProduct(item, entry.product);
        }
      });
      list.appendChild(li);
    });
    list.hidden = false;
  }

  /* Load the storefront catalog once (paginated) and cache it. Filtering is
     done client-side, so this works regardless of the Search & Discovery app. */
  ensureCatalog() {
    if (this.catalogPromise) return this.catalogPromise;
    this.catalogPromise = (async () => {
      const all = [];
      for (let page = 1; page <= 10; page += 1) {
        let products = [];
        try {
          const res = await fetch(`/products.json?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
          if (!res.ok) break;
          const data = await res.json();
          products = data.products || [];
        } catch (error) {
          break;
        }
        products.forEach((p) => {
          all.push({
            title: p.title,
            handle: p.handle,
            variants: (p.variants || []).map((v) => ({ title: v.title, sku: v.sku || '' })),
          });
        });
        if (products.length < 250) break;
      }
      this.catalog = all;
      return all;
    })();
    return this.catalogPromise;
  }

  applyOrderItem(item, entry) {
    item.querySelector('[data-product-search]').value = entry.product;
    item.querySelector('[data-product-value]').value = entry.product;
    item.querySelector('[data-sku-input]').value = entry.sku;
    const qty = item.querySelector('[data-qty-input]');
    if (!qty.value) qty.value = entry.qty;
    this.hideVariantSelect(item);
    this.updateWarning(item);
    this.validateQuantity(item);
  }

  applyCatalogProduct(item, product) {
    const skuInput = item.querySelector('[data-sku-input]');
    const productValue = item.querySelector('[data-product-value]');
    const variants = product.variants || [];
    item.querySelector('[data-product-search]').value = product.title;

    if (variants.length > 1) {
      this.showVariantSelect(item, product, variants);
      const first = variants[0];
      skuInput.value = first.sku || '';
      productValue.value = `${product.title} - ${first.title}`;
    } else {
      const v = variants[0] || {};
      this.hideVariantSelect(item);
      skuInput.value = v.sku || '';
      productValue.value =
        v.title && v.title !== 'Default Title' ? `${product.title} - ${v.title}` : product.title;
    }
    this.updateWarning(item);
  }

  showVariantSelect(item, product, variants) {
    const select = item.querySelector('[data-variant-select]');
    if (!select) return;
    select.innerHTML = '';
    variants.forEach((v, i) => {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = v.title;
      opt.dataset.sku = v.sku || '';
      opt.dataset.product = `${product.title} - ${v.title}`;
      select.appendChild(opt);
    });
    select.hidden = false;
  }

  hideVariantSelect(item) {
    const select = item.querySelector('[data-variant-select]');
    if (select) {
      select.hidden = true;
      select.innerHTML = '';
    }
  }

  /* Non-blocking warning when the SKU isn't part of the matched order. */
  updateWarning(item) {
    const warn = item.querySelector('[data-warn="product"]');
    if (!warn) return;
    const sku = item.querySelector('[data-sku-input]').value.trim();
    if (this.matchedOrder && sku) {
      const inOrder = Object.keys(this.matchedOrder.skus).some((k) => k.toLowerCase() === sku.toLowerCase());
      if (!inOrder) {
        warn.textContent = `Heads up: this product isn't part of order ${this.matchedOrder.name}. You can still submit it.`;
        warn.hidden = false;
        return;
      }
    }
    warn.hidden = true;
    warn.textContent = '';
  }

  /* ---- Per-item validation ---- */

  itemQuantity(item) {
    const value = item.querySelector('[data-qty-input]').value.trim();
    return value === '' ? 0 : Number(value);
  }

  itemProduct(item) {
    const hidden = item.querySelector('[data-product-value]').value.trim();
    return hidden || item.querySelector('[data-product-search]').value.trim();
  }

  showError(scope, field, message) {
    const error = scope.querySelector(`[data-error="${field}"]`);
    if (error) {
      error.textContent = message || '';
      error.hidden = !message;
    }
    return !message;
  }

  validateQuantity(item) {
    const raw = item.querySelector('[data-qty-input]').value.trim();
    const qty = raw === '' ? 0 : Number(raw);
    if (Number.isNaN(qty) || !Number.isInteger(qty) || qty < 1) {
      return this.showError(item, 'quantity', 'Enter a whole number of 1 or more.');
    }
    return this.showError(item, 'quantity', '');
  }

  validateReason(item) {
    const value = item.querySelector('[data-reason-select]').value;
    return this.showError(item, 'reason', value === REASONS_DEFAULT ? 'Please select a return reason.' : '');
  }

  validateProduct(item) {
    const hasProduct = this.itemProduct(item) || item.querySelector('[data-sku-input]').value.trim();
    return this.showError(item, 'product', hasProduct ? '' : 'Enter a product or SKU.');
  }

  validatePhoto(item) {
    const file = item.querySelector('[data-photo-input]').files[0];
    if (!file) {
      return this.showError(item, 'photo', 'Please attach a photo of the product.');
    }
    const extension = file.name.split('.').pop().toLowerCase();
    if (!VALID_EXTENSIONS.includes(extension) || !VALID_MIME_TYPES.includes(file.type)) {
      return this.showError(item, 'photo', 'Invalid file type. Accepted formats: JPG, PNG, HEIC.');
    }
    if (file.size > this.maxFileMb * 1024 * 1024) {
      return this.showError(item, 'photo', `File is too large. Max file size: ${this.maxFileMb} MB.`);
    }
    return this.showError(item, 'photo', '');
  }

  /* ---- Photo upload (per item) ---- */

  handlePhotoChange(item) {
    delete item.dataset.photoUrl;
    this.setPhotoStatus(item, '');
    const file = item.querySelector('[data-photo-input]').files[0];
    const nameLabel = item.querySelector('[data-photo-name]');
    if (nameLabel) nameLabel.textContent = file ? file.name : '';
    if (!this.validatePhoto(item) || !file) return;
    this.startUpload(item, file);
  }

  async startUpload(item, file) {
    this.uploadSeq += 1;
    const seq = String(this.uploadSeq);
    item.dataset.uploadSeq = seq;
    this.setPhotoStatus(item, 'Uploading photo…');
    this.uploadsInFlight += 1;
    try {
      const url = await this.uploadPhoto(file);
      if (item.dataset.uploadSeq === seq) {
        item.dataset.photoUrl = url;
        this.setPhotoStatus(item, 'Photo uploaded ✓');
      }
    } catch (error) {
      if (item.dataset.uploadSeq === seq) {
        this.setPhotoStatus(item, '');
        this.showError(item, 'photo', 'Photo upload failed. Choose the file again to retry.');
      }
    } finally {
      this.uploadsInFlight -= 1;
    }
  }

  setPhotoStatus(item, message) {
    const status = item.querySelector('[data-photo-status]');
    if (status) {
      status.textContent = message || '';
      status.hidden = !message;
    }
  }

  async uploadPhoto(file) {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', this.dataset.uploadPreset);
    const response = await fetch(`https://api.cloudinary.com/v1_1/${this.dataset.cloudName}/image/upload`, {
      method: 'POST',
      body: data,
    });
    if (!response.ok) throw new Error(`Photo upload failed with status ${response.status}`);
    const result = await response.json();
    if (!result.secure_url) throw new Error('Photo upload did not return a URL.');
    return result.secure_url;
  }

  /* ---- Submit ---- */

  prepareSubmission() {
    const orderValue = this.orderInput.value.trim();
    let valid = this.showError(this.form, 'order', orderValue ? '' : 'Please enter your order number.');

    const items = Array.from(this.form.querySelectorAll('[data-return-item]'));
    const returning = items.filter((item) => this.itemQuantity(item) >= 1);

    items.forEach((item) => {
      if (this.itemQuantity(item) < 1) return;
      const okQty = this.validateQuantity(item);
      const okReason = this.validateReason(item);
      const okProduct = this.validateProduct(item);
      const okPhoto = this.validatePhoto(item);
      if (!(okQty && okReason && okProduct && okPhoto)) valid = false;
    });

    if (returning.length === 0) {
      this.showFormError('Add at least one product with a quantity of 1 or more.');
      return false;
    }
    if (!valid) {
      this.showFormError('Please fix the errors above before submitting.');
      return false;
    }

    const contactForm = this.querySelector('#ReturnContactForm');
    if (!this.dataset.cloudName || !this.dataset.uploadPreset || !contactForm) {
      this.showFormError('The return form is not configured yet. Please contact us instead.');
      return false;
    }
    if (this.uploadsInFlight > 0) {
      this.showFormError('Your photos are still uploading — please wait a moment and try again.');
      return false;
    }
    if (returning.some((item) => !item.dataset.photoUrl)) {
      this.showFormError('A photo could not be uploaded. Choose the file again to retry.');
      return false;
    }

    const fields = contactForm.querySelector('[data-contact-fields]');
    fields.innerHTML = '';
    const addField = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `contact[${name}]`;
      input.value = value;
      fields.appendChild(input);
    };

    addField('Order Number', orderValue);
    addField('Customer Name', this.dataset.customerName || '');
    addField('Order Verified', this.matchedOrder ? `Yes — matches ${this.matchedOrder.name}` : 'No — order not found, manual review');

    returning.forEach((item, index) => {
      const n = index + 1;
      const sku = item.querySelector('[data-sku-input]').value.trim();
      const inOrder = this.matchedOrder && sku
        ? Object.keys(this.matchedOrder.skus).some((k) => k.toLowerCase() === sku.toLowerCase())
        : false;
      addField(`Item ${n} - Product`, this.itemProduct(item));
      addField(`Item ${n} - SKU`, sku);
      addField(`Item ${n} - Quantity to Return`, String(this.itemQuantity(item)));
      addField(`Item ${n} - Reason`, item.querySelector('[data-reason-select]').value);
      addField(`Item ${n} - Photo`, item.dataset.photoUrl);
      if (this.matchedOrder && !inOrder) {
        addField(`Item ${n} - Note`, 'Not part of the matched order');
      }
    });
    addField(
      'body',
      this.matchedOrder
        ? `Return request for order ${orderValue} (${returning.length} item(s)).`
        : `Return request for order ${orderValue} — order NOT found on the customer's account, please review manually (${returning.length} item(s)).`
    );

    this.showFormError('');
    return true;
  }

  showFormError(message) {
    const banner = this.form ? this.form.querySelector('[data-return-form-error]') : null;
    if (!banner) return;
    banner.textContent = message || '';
    banner.hidden = !message;
  }

  showSuccess() {
    const success = this.querySelector('[data-return-success]');
    const form = this.querySelector('[data-return-form]');
    if (form) form.hidden = true;
    if (!success) return;
    success.hidden = false;
    success.focus();
    success.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  escape(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }
}

if (!customElements.get('return-request-app')) {
  customElements.define('return-request-app', ReturnRequestApp);
}
